/* ---------------------------------------------------------------------------
   Media adapter — the seam between the screens and `src/lib/**`.

   The screens only ever talk to the `MediaAdapter` interface in contracts.ts.
   This file is the single implementation of it, and it is a THIN wrapper: every
   piece of real behaviour lives in `src/lib`, which owns the session's one
   AudioContext, the frame-accurate video timing, the MediaRecorder lifecycle
   and the clip validation.

   Nothing here may construct an AudioContext. `src/lib/audio.ts` owns exactly
   one for the life of the page — a second one is the classic way to end up with
   a silent playback phase on an iPhone, and SPEC.md calls it out by name.
   --------------------------------------------------------------------------- */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { splitSegment, type SubChunk } from "../../convex/lib/game";
import { isAudioSupported, unlockAudio } from "../lib/audio";
import { captureWhile } from "../lib/capture";
import { createMicMeter, type MicMeter } from "../lib/micLevel";
import { preferredAudioMime } from "../lib/mobile";
import {
  loadDubAudio,
  playDub as playDubSegment,
  playOriginal as playOriginalSegment,
} from "../lib/playback";
import { uploadToR2WithRetry } from "../lib/upload";
import {
  formatDuration,
  formatMegabytes,
  validateClip,
} from "../lib/validateClip";
import { playUntil, seekTo } from "../lib/videoTiming";
import type { ClipProbe, MediaAdapter } from "./contracts";

/**
 * Build the real adapter. One instance per page — `MediaProvider` memoises it,
 * because the microphone stream and the mic meter are per-session resources.
 */
export function createMediaAdapter(): MediaAdapter {
  let stream: MediaStream | null = null;
  let meter: MicMeter | null = null;
  let unlocked = false;
  let mimeType: string | null = null;

  /** Aborts whatever is currently playing — a dub or the original. */
  let playbackAbort: AbortController | null = null;

  return {
    get unlocked() {
      return unlocked;
    },
    get micReady() {
      return stream !== null;
    },

    /**
     * Unlock the session's audio. MUST be *initiated* inside a real tap: the
     * caller fires this synchronously and awaits it afterwards, so the user
     * activation is still live when the AudioContext resumes.
     */
    async unlock() {
      if (!isAudioSupported()) {
        unlocked = false;
        return;
      }
      unlocked = await unlockAudio();
    },

    async armMic() {
      if (stream) return;
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      mimeType = preferredAudioMime();
      meter = createMicMeter(stream);
    },

    micLevel() {
      return meter ? meter.sample().level : 0;
    },

    /**
     * The refresher pass: the ONE pass with the original audio on. Seeks first
     * and waits for the frame to land, so the player hears the sub-chunk from
     * its real start rather than from wherever the element happened to be.
     */
    async playRefresher(video, chunk) {
      await seekTo(video, chunk.start);
      video.muted = false;
      video.volume = 1;
      try {
        await playUntil(video, chunk.end);
      } finally {
        // Muted from here on no matter how this ended — the recording pass must
        // never leak the original audio into the microphone.
        video.muted = true;
      }
    },

    /**
     * The recording pass: video MUTED, microphone captured for exactly as long
     * as the sub-chunk actually plays. Bounding the take by real playback
     * rather than a wall-clock timer is what keeps the dub aligned with the
     * picture — a stalled decode would otherwise slide every take late.
     */
    async recordChunk(video, chunk) {
      if (!stream) throw new Error("The microphone is not armed.");

      video.muted = true;
      video.volume = 0;
      await seekTo(video, chunk.start);

      const captured = await captureWhile(stream, mimeType, async () => {
        await playUntil(video, chunk.end);
      });
      mimeType = captured.mimeType;
      return captured.blob;
    },

    /**
     * Video muted, the performer's recorded chunks scheduled against the video
     * clock through the one AudioContext. Never muxes: the element is the
     * clock and the chunks stay timestamps.
     */
    async playDub(video, opts) {
      // No audio means the signed URLs haven't landed yet. Playing on would
      // show the picture in silence and report success, which reads to a player
      // as "this dub is broken" rather than "wait a moment".
      if (opts.audioUrls.length === 0) {
        throw new Error("That dub's audio hasn't loaded yet. Give it a second and tap play.");
      }

      playbackAbort?.abort();
      const controller = new AbortController();
      playbackAbort = controller;

      const subChunks = splitSegment(opts.start, opts.length);
      const buffers = await loadDubAudio(opts.audioUrls, { signal: controller.signal });
      if (controller.signal.aborted) return;

      try {
        await playDubSegment(video, buffers, subChunks, { signal: controller.signal });
      } finally {
        if (playbackAbort === controller) playbackAbort = null;
      }
    },

    /**
     * The original pass, audio ON. The abort signal IS the skip vote: when a
     * strict majority lands, the caller stops this and playback ends at once.
     *
     * Requires the element to have been primed inside the tap that started the
     * phase — an unprimed element rejects with `MediaPlaybackError` code
     * "blocked", which is recoverable on a second tap.
     */
    async playOriginal(video, opts) {
      playbackAbort?.abort();
      const controller = new AbortController();
      playbackAbort = controller;

      try {
        await playOriginalSegment(
          video,
          { start: opts.start, end: opts.start + opts.length },
          { signal: controller.signal },
        );
      } finally {
        if (playbackAbort === controller) playbackAbort = null;
      }
    },

    stopPlayback() {
      playbackAbort?.abort();
      playbackAbort = null;
    },

    /**
     * Validate before a byte is uploaded. Never throws — a rejection comes back
     * as `ok: false` with text written for a player, not a developer.
     */
    async inspectFile(file: File): Promise<ClipProbe> {
      // Our own preview URL: validateClip revokes the one it makes internally,
      // and the segment picker needs this to outlive the probe.
      const previewUrl = URL.createObjectURL(file);
      const result = await validateClip(file);

      if (!result.ok) {
        return {
          ok: false,
          durationSec: result.durationSec ?? 0,
          hasAudio: false,
          reasons: [result.message],
          previewUrl,
        };
      }

      return {
        ok: true,
        durationSec: result.durationSec,
        hasAudio: true,
        reasons: [],
        previewUrl,
      };
    },

    async upload(file, url, onProgress) {
      await uploadToR2WithRetry(file, url, (progress) => onProgress(progress.fraction), {
        contentType: file.type || undefined,
      });
    },
  };
}

/** Re-exported so screens can format sizes/durations the same way as the probe. */
export { formatDuration, formatMegabytes };

const MediaContext = createContext<MediaAdapter | null>(null);

export function MediaProvider({
  adapter,
  children,
}: {
  adapter?: MediaAdapter;
  children: ReactNode;
}) {
  const fallback = useMemo(createMediaAdapter, []);
  return (
    <MediaContext.Provider value={adapter ?? fallback}>{children}</MediaContext.Provider>
  );
}

export function useMedia(): MediaAdapter {
  const adapter = useContext(MediaContext);
  if (!adapter) throw new Error("useMedia must be used inside <MediaProvider>.");
  return adapter;
}

/**
 * Samples `micLevel()` on animation frames while `active`. Returns 0–1.
 * Only re-renders when the level moves enough to be visible, so a 60fps meter
 * does not drag the whole recording screen through React on every frame.
 */
export function useMicLevel(active: boolean): number {
  const media = useMedia();
  const [level, setLevel] = useState(0);
  const last = useRef(0);

  useEffect(() => {
    if (!active) {
      setLevel(0);
      return;
    }
    let raf = 0;
    const tick = () => {
      const next = media.micLevel();
      if (Math.abs(next - last.current) > 0.01) {
        last.current = next;
        setLevel(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, media]);

  return level;
}

export type { SubChunk };
